import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import AddPlantDialog from "../components/AddPlantDialog";
import PlantCard from "../components/PlantCard";
import PlantTile, { GRID_CLASS } from "../components/PlantTile";
import { toast } from "sonner";
import HomeHero from "../components/HomeHero";
import { readCache, writeCache, cachedLoad } from "../lib/pageCache";
import Footer from "../components/Footer";
import Announcements from "../components/Announcements";
import PendingBanner, { usePendingCount } from "../components/PendingApprovals";
import { startTour } from "../lib/tour";
import CriticalEventDialog from "../components/CriticalEventDialog";
import { QuickActions, MoreMenu } from "../components/DashboardParts";
import { urgentByPlant } from "../lib/reminders";
import { Button } from "../components/ui/button";
import { Plant as PlantIcon, PlayCircle } from "@phosphor-icons/react";
import { authApi, listPlants, getStats, getLastEvents, listReminders, listStreaks } from "../lib/api";
import { sortPlants } from "../lib/theme";
import LocationGroups from "../components/LocationGroups";
import LocationsDialog from "../components/LocationsDialog";
import PlantRow from "../components/PlantRow";
import CollectionToolbar, { SORTS } from "../components/CollectionToolbar";
import Highlights from "../components/Highlights";
import NewArrivals from "../components/NewArrivals";
import { useSite } from "../lib/site";
import { acuteBurst } from "../lib/burst";
import { useAuth } from "../context/AuthContext";
const ScanDialog = lazy(() => import("../components/ScanSheet"));


const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

export default function Dashboard() {
  const { user, setUser, isFavorite, toggleFavorite } = useAuth();
  const site = useSite();
  const navigate = useNavigate();
  const [ver, setVer] = useState(0);
  const [view, setView] = useState(() => user?.prefs?.home_view || "all");
  const [sortPick, setSortBy] = useState(() => { const v = localStorage.getItem("gh-sort"); return SORTS.some(([k]) => k === v) ? v : null; });
  const sortBy = sortPick || site.default_sort || "az";
  const chooseSort = (v) => { setSortBy(v); localStorage.setItem("gh-sort", v); };
  const [layoutPick, setLayout] = useState(() => user?.prefs?.card_layout || null);
  const layout = layoutPick || site.default_layout || "large";
  const chooseLayout = (v) => {
    setLayout(v);
    authApi.updateMe({ prefs: { ...(user?.prefs || {}), card_layout: v } }).then(setUser).catch(() => toast.error("Couldn't save your layout"));
  };
  const cached = readCache("dash");
  const [plants, setPlants] = useState(cached?.plants || []);
  const [stats, setStats] = useState(cached?.stats || { total_plants: 0, watered_this_week: 0, events_today: 0 });
  const [loading, setLoading] = useState(!cached);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [lastWaterMap, setLastWaterMap] = useState(cached?.lastWater || {});
  const [reminders, setReminders] = useState(readCache("reminders") || []);
  const [acuteOpen, setAcuteOpen] = useState(false);
  const [scanOpen, setScanOpenRaw] = useState(false);
  const [scanUsed, setScanUsed] = useState(false);
  const setScanOpen = (v) => { if (v) setScanUsed(true); setScanOpenRaw(v); };
  const [locsOpen, setLocsOpen] = useState(false);
  const quickAction = (k, e) => (k === "tour" ? setTimeout(startTour, 150) : k === "plant" ? setAddOpen(true) : k === "acute" ? (acuteBurst(e), setTimeout(() => setAcuteOpen(true), 260)) : k === "scan" ? setScanOpen(true) : navigate(k === "care" ? "/batch" : "/labels"));
  const pendingCount = usePendingCount(user?.role === "admin");

  const loadReminders = () => cachedLoad("reminders", listReminders, setReminders).catch(() => {});

  const refresh = async () => {
    if (!readCache("dash")) setLoading(true);
    setVer((v) => v + 1);
    try {
      const [p, s, l] = await Promise.all([listPlants(), getStats(), getLastEvents(), loadReminders()]);
      setPlants(p);
      setStats(s);
      setLastWaterMap(l);
      writeCache("dash", { plants: p, stats: s, lastWater: l });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const open = () => setAddOpen(true);
    window.addEventListener("gh:add-plant", open);
    return () => window.removeEventListener("gh:add-plant", open);
  }, []);

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user && !user.onboarded_at) startTour();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dueMap = useMemo(() => urgentByPlant(reminders), [reminders]);
  const [streaks, setStreaks] = useState({});
  useEffect(() => { listStreaks().then(setStreaks).catch(() => {}); }, [reminders]);
  const schedule = useMemo(() => {
    const m = {};
    reminders.forEach((r) => { m[r.plant_id] = (m[r.plant_id] ?? true) && r.status !== "overdue"; });
    reminders.forEach((r) => { if (r.event_type === "watering" && !r.last_at) m[r.plant_id] = false; });
    return m;
  }, [reminders]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const weekAgo = Date.now() - 7 * 86400000;
    const base = plants.filter((p) => (view === "deceased" ? p.status === "deceased" : view === "all" ? true : p.status !== "deceased")).filter((p) => view === "all" || view === "deceased" || (view === "favorites" ? isFavorite(p.id) : view === "critical" ? p.critical_active : view === "new" ? new Date(p.created_at).getTime() >= weekAgo : (p.status || "active") === view));
    if (!q) return base;
    return base.filter(
      (p) =>
        p.nickname?.toLowerCase().includes(q) ||
        p.species?.toLowerCase().includes(q) ||
        p.scientific_name?.toLowerCase().includes(q) ||
        p.location?.toLowerCase().includes(q) ||
        p.plant_code?.toLowerCase().includes(q)
    );
  }, [query, plants, view, isFavorite]);

  const ordered = useMemo(() => sortPlants(filtered, sortBy, dueMap), [filtered, sortBy, dueMap]);

  const card = (p, i) => (
    <div key={p.id} className="rise-in" style={{ animationDelay: `${Math.min(i, 11) * 45}ms` }}>{layout === "list" ? <PlantRow plant={p} reminder={dueMap[p.id]} lastWatered={lastWaterMap[p.id]?.watering} favorite={isFavorite(p.id)} onToggleFavorite={toggleFavorite} priority={i < 12} /> : layout === "compact" ? <PlantTile plant={p} favorite={isFavorite(p.id)} priority={i < 10} /> : <PlantCard plant={p} reminder={dueMap[p.id]} lastWatered={lastWaterMap[p.id]?.watering} favorite={isFavorite(p.id)} onToggleFavorite={toggleFavorite} streak={streaks[p.id] || 0} onSchedule={!!schedule[p.id]} priority={i < 8} />}</div>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <NavBar onAddPlant={() => setAddOpen(true)} onCriticalCreated={refresh} />

      <main className="flex-1 w-full max-w-6xl mx-auto px-6 py-8">
        <PendingBanner count={pendingCount} />
        <Announcements />

        <div className="pt-1 mb-10">
          <HomeHero user={user} showToday={site.show_today} version={ver} reminders={reminders}
            greetingText={site.greeting ? site.greeting.replace("{name}", user?.name?.split(" ")[0] || "") : `${greeting()}, ${user?.name?.split(" ")[0]}`}
            onCareChanged={() => { loadReminders(); getLastEvents().then(setLastWaterMap); setVer((v) => v + 1); }}
            actions={site.show_quick_actions
              ? <QuickActions onAction={quickAction} />
              : <MoreMenu onAction={quickAction} items={[{ key: "tour", label: "Take the tour", Icon: PlayCircle, testid: "open-tour-btn" }]} />} />
          {site.show_highlights && <Highlights version={ver} />}
        </div>

        {site.show_new_arrivals !== false && <NewArrivals plants={plants} onSeeAll={() => { setView("new"); document.querySelector('[data-testid="collection-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />}

        <section data-testid="collection-section">
          <div className="flex items-end justify-between gap-4 mb-1">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight" data-testid="collection-title">Collection</h2>
            <span className="text-sm text-muted-foreground tabular-nums pb-1" data-testid="plants-count">{filtered.length === plants.length ? `${plants.length} plants` : `${filtered.length} of ${plants.length}`}</span>
          </div>
          <CollectionToolbar query={query} setQuery={setQuery} onScan={() => setScanOpen(true)} view={view} setView={setView}
            sortBy={sortBy} setSort={chooseSort} layout={layout} setLayout={chooseLayout} onManageLocations={() => setLocsOpen(true)} defaultSort={site.default_sort || "az"} />

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-3xl bg-card overflow-hidden">
                <div className="aspect-[4/3] bg-muted animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-2/3 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-muted rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          plants.length ? <p className="text-sm text-muted-foreground text-center py-16" data-testid="no-matches">{view === "new" ? "No plants added in the last 7 days." : "No plants match this view."}</p> : <EmptyState onAdd={() => setAddOpen(true)} />
        ) : sortBy === "location" ? (
          <LocationGroups plants={ordered} renderCard={(p, i) => card(p, i)} gridClass={GRID_CLASS[layout]} />
        ) : (
          <div className={GRID_CLASS[layout]} data-testid="plants-grid" data-layout={layout}>
            {ordered.map((p, i) => card(p, i))}
          </div>
        )}
        </section>
      </main>
      <CriticalEventDialog open={acuteOpen} onOpenChange={setAcuteOpen} onCreated={refresh} />
      <Footer />
      {scanUsed && <Suspense fallback={null}><ScanDialog open={scanOpen} onOpenChange={setScanOpen} /></Suspense>}
      <LocationsDialog open={locsOpen} onOpenChange={setLocsOpen} onChanged={refresh} />

      <AddPlantDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => refresh()}
      />
    </div>
  );
}


function EmptyState({ onAdd }) {
  return (
    <div className="rounded-3xl border border-dashed border-border bg-card/50 p-14 text-center">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
        <PlantIcon weight="duotone" className="h-7 w-7 text-primary" />
      </div>
      <h3 className="mt-5 text-xl font-semibold tracking-tight">Start your collection</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        Add your first plant. Add a photo, name and species to start its care record.
      </p>
      <Button onClick={onAdd} className="mt-6 rounded-full" data-testid="empty-add-plant-btn">
        Add first plant
      </Button>
    </div>
  );
}
